import {
  Col,
  DatePicker,
  Input,
  Modal,
  notification,
  Radio,
  Row,
  Select,
  Spin,
  Switch,
} from "antd";
import axios from "axios";
import { Formik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { Creators as SupplierActions } from "../../../store/ducks/supplier";
import { Creators as ReportActions } from "../../../store/ducks/report";

import { getListOfClientsDropDown } from "../../../services/managementService";
import { processReportConfinementResult } from "../../../services/reportService";
import { indexDropDownSaleScenario } from "../../../services/saleScenarioService";
import ButtonStandard from "../../utils/button";
import { Container } from "./styles";
import { getLotIndexDropDown } from "../../../services/lotService";
import fileReader from "../../../utils/fileReader";
import {
  momentTimeZoneHour,
  momentTimeZoneZeroHour,
} from "../../../utils/dateUtils";
import NumberFormat from "react-number-format";

// Services

const INITIAL_FORM_STATE = {
  startDate: null,
  endDate: null,
  clientId: null,
  supplierId: null,
  saleScenarioIds: [],
  lotIds: [],
  documentNumber: null,
  showDeathData: false,
  showOriginData: false,
  enableLotResult: false,
  dailyOperationalCostMode: "BOVEXO_COSTING",
  dailyOperationalCostValue: null,
  additionalSlaughterCostValue: null,
};

const validateSchema = Yup.object().shape({
  startDate: Yup.date().required(),
  endDate: Yup.date().required(),
  lotIds: Yup.array(Yup.string())
    .nullable()
    .when(["showDeathData"], {
      is: (showDeathData) => showDeathData === true,
      then: Yup.array(Yup.string()).test({
        test: (arr) => arr.length > 0,
      }),
      otherwise: Yup.array(Yup.string()).nullable(),
    }),
  dailyOperationalCostMode: Yup.string().required(),
  dailyOperationalCostValue: Yup.number()
    .nullable()
    .notRequired()
    .when(["dailyOperationalCostMode"], {
      is: (mode) => mode === "USER_DEFINED",
      then: Yup.number().min(0.01).required(),
      otherwise: Yup.number().nullable().notRequired(),
    }),
  additionalSlaughterCostValue: Yup.number().nullable().notRequired(),
});

const ConfinementResultReportModal = () => {
  const formik = useRef();
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);
  const {
    listDrowpDown: suppliers,
    isLoadingDropDown: isLoadingDropDownSuppliers,
  } = useSelector((state) => state.supplier);

  const { isModalConfinementResultReportVisible: modalVisible } = useSelector(
    (state) => state.report,
  );

  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [lots, setLots] = useState([]);
  const [clients, setClients] = useState([]);
  const [saleScenarios, setSaleScenarios] = useState([]);

  const cleanFields = () => {
    formik.current.resetForm(INITIAL_FORM_STATE);
  };

  const handleCancel = useCallback(() => {
    cleanFields();
    dispatch(ReportActions.hideModal());
  }, [dispatch]);

  const handleSubmit = async (values, actions) => {
    setIsLoading(true);
    notification.info({
      message:
        "O seu relatório está sendo preparado e logo será feito o download! Por favor, não feche esta aba do navegador.",
    });
    setTimeout(() => {
      setIsLoading(false);
      cleanFields();
      dispatch(ReportActions.hideModal());
    }, 100);
    try {
      const { data } = await processReportConfinementResult({
        groupId,
        farmId,
        body: values,
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([data]));
      let link = document.createElement("a");
      link.href = downloadUrl;
      const dayFormat = momentTimeZoneHour().format("YYYYMMDD");
      const hourFormat = momentTimeZoneHour().format("HHmm");
      link.setAttribute(
        "download",
        `Resultado_do_Confinamento_${dayFormat}_${hourFormat}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();

      notification.success({
        message: "Relatório gerado com sucesso.",
      });
    } catch (error) {
      if (error?.response) {
        try {
          const { data } = error.response;
          // Read file
          const file = await fileReader(data);

          if (typeof file === "string") {
            // Parse content and retrieve 'message'
            const result = JSON.parse(file);
            if (result.code === 5035) {
              notification.error({
                message:
                  "Não foram encontradas vendas com os filtros informados.",
              });
            } else {
              notification.error({
                message:
                  "Houve um erro ao gerar relatório. Entre em contato com o suporte",
              });
            }
          }
        } catch (readError) {
          notification.error({
            message:
              "Houve um erro ao gerar relatório. Entre em contato com o suporte",
          });
        }
      } else {
        notification.error({
          message:
            "Houve um erro ao gerar relatório. Entre em contato com o suporte",
        });
      }
    }
  };

  // Effects
  useEffect(() => {
    let signal = axios.CancelToken.source();
    async function fetch() {
      setIsLoading(true);
      try {
        dispatch(
          SupplierActions.getDropdownSuppliers(
            groupId,
            farmId,
            "CattleFarmer",
            true,
          ),
        );
        const [
          {
            data: { results: resultsClient },
          },
          {
            data: { results: resultsSaleScenarios },
          },
          {
            data: { results: lotsResults },
          },
        ] = await Promise.all([
          getListOfClientsDropDown({
            groupId,
            farmId,
            onlyActives: true,
          }),
          indexDropDownSaleScenario({
            groupId,
            farmId,
          }),
          getLotIndexDropDown({
            groupId,
            farmId,
          }),
        ]);
        setClients(resultsClient);
        const filteredOnlyPartialSaledOrSaled = resultsSaleScenarios.filter(
          (ss) =>
            ss.status === "PartialSold" ||
            ss.status === "Sold" ||
            ss.status === "PS" ||
            ss.status === "S" ||
            ss.status === "PartialCanceled" ||
            ss.status === "PC",
        );
        setSaleScenarios(filteredOnlyPartialSaledOrSaled);
        setLots(
          lotsResults.filter(
            (lot) => lot.status !== "Inactive" || lot.status === "I",
          ),
        );
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }

    if (modalVisible === true) {
      fetch();
    }

    return () => {
      signal.cancel("Cancelou");
    };
  }, [dispatch, farmId, groupId, modalVisible]);

  return (
    <Modal
      width={550}
      visible={modalVisible}
      centered
      closable={false}
      footer={null}
      title={translation.confinementResultReport.form.title}
    >
      <Container>
        <Spin spinning={isLoading}>
          {/* Form */}
          <Row type="flex" gutter={8}>
            <Col span={24}>
              <Formik
                ref={formik}
                initialValues={INITIAL_FORM_STATE}
                enableReinitialize={true}
                onSubmit={handleSubmit}
                validationSchema={validateSchema}
                render={({
                  values,
                  errors,
                  submitCount,
                  handleSubmit,
                  setFieldValue,
                }) => (
                  <form onSubmit={handleSubmit} autoComplete="off">
                    {/* Body */}
                    <div className="body">
                      {Object.entries(errors).length > 0 && submitCount > 0 && (
                        <Row type="flex" justify="center" align="middle">
                          <label className="error">
                            {translation.error.formError}
                          </label>
                        </Row>
                      )}
                      {/* salesPeriod */}
                      <Row type="flex" justify="start" gutter={8}>
                        <Col span={24}>
                          <Row>
                            <label
                              className={
                                (errors.startDate || errors.endDate) &&
                                submitCount > 0
                                  ? "error"
                                  : ""
                              }
                            >
                              {
                                translation.confinementResultReport.form
                                  .salesPeriod
                              }
                              *
                            </label>
                          </Row>
                          <Row type="flex" gutter={8} align="middle">
                            <Col span={8}>
                              <DatePicker
                                name="startDate"
                                format={"DD/MM/YYYY"}
                                value={
                                  values.startDate
                                    ? momentTimeZoneZeroHour(values.startDate)
                                    : undefined
                                }
                                disabledDate={(currentDate) =>
                                  currentDate.isAfter(
                                    momentTimeZoneZeroHour(),
                                    "days",
                                  )
                                }
                                allowClear={false}
                                onChange={(date) =>
                                  setFieldValue(
                                    "startDate",
                                    momentTimeZoneZeroHour(date),
                                  )
                                }
                              />
                            </Col>
                            <Col span={2}>até</Col>
                            <Col span={8}>
                              <DatePicker
                                name="endDate"
                                format={"DD/MM/YYYY"}
                                value={
                                  values.endDate
                                    ? momentTimeZoneZeroHour(values.endDate)
                                    : undefined
                                }
                                disabledDate={(currentDate) =>
                                  currentDate.isBefore(values.startDate) ||
                                  currentDate.isAfter(
                                    momentTimeZoneZeroHour(),
                                    "days",
                                  )
                                }
                                allowClear={false}
                                onChange={(date) =>
                                  setFieldValue(
                                    "endDate",
                                    momentTimeZoneZeroHour(date),
                                  )
                                }
                              />
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      {/* clientId & supplierId */}
                      <Row type="flex" justify="start" gutter={8}>
                        <Col span={12}>
                          <Row>
                            <label>
                              {
                                translation.confinementResultReport.form
                                  .clientId
                              }
                            </label>
                          </Row>
                          <Row type="flex">
                            <Select
                              name="clientId"
                              value={values.clientId || undefined}
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                option.props.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              allowClear
                              onDeselect={() => setFieldValue("clientId", null)}
                              placeholder={translation.defaultSelectPlaceholder}
                              onChange={(value) =>
                                setFieldValue("clientId", value)
                              }
                            >
                              {clients.map((c) => (
                                <Select.Option value={c.id} key={c.id}>
                                  {c.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Row>
                        </Col>
                        <Col span={12}>
                          <Row>
                            <label>
                              {
                                translation.confinementResultReport.form
                                  .supplierId
                              }
                            </label>
                          </Row>
                          <Row type="flex">
                            <Select
                              name="supplierId"
                              value={values.supplierId || undefined}
                              showSearch
                              optionFilterProp="children"
                              filterOption={(input, option) =>
                                option.props.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              placeholder={translation.defaultSelectPlaceholder}
                              loading={isLoadingDropDownSuppliers}
                              allowClear
                              onDeselect={() =>
                                setFieldValue("supplierId", null)
                              }
                              onChange={(value) =>
                                setFieldValue("supplierId", value)
                              }
                            >
                              {suppliers.map((c) => (
                                <Select.Option value={c.id} key={c.id}>
                                  {c.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Row>
                        </Col>
                      </Row>
                      {/* saleScenarioId & documentNumber */}
                      <Row type="flex" justify="start" gutter={8}>
                        <Col span={12}>
                          <Row>
                            <label>
                              {
                                translation.confinementResultReport.form
                                  .saleScenarioId
                              }
                            </label>
                          </Row>
                          <Row type="flex">
                            <Select
                              name="saleScenarioIds"
                              mode="multiple"
                              value={values.saleScenarioIds || undefined}
                              placeholder={translation.defaultSelectPlaceholder}
                              onChange={(pValues) => {
                                setFieldValue("saleScenarioIds", pValues);
                              }}
                            >
                              {saleScenarios.map((c) => (
                                <Select.Option value={c.id} key={c.id}>
                                  {c.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Row>
                        </Col>
                        <Col span={12}>
                          <Row>
                            <label>
                              {
                                translation.confinementResultReport.form
                                  .documentNumber
                              }
                            </label>
                          </Row>
                          <Row type="flex">
                            <Input
                              name="documentNumber"
                              value={values.documentNumber}
                              placeholder={translation.defaultPlaceholder}
                              onChange={(e) =>
                                setFieldValue("documentNumber", e.target.value)
                              }
                            />
                          </Row>
                        </Col>
                      </Row>
                      {/* d. Diária Operacional */}
                      <Row type="flex" gutter={8}>
                        <Col span={24}>
                          <Row>
                            <label
                              className={
                                (errors.dailyOperationalCostMode ||
                                  errors.dailyOperationalCostValue) &&
                                submitCount > 0
                                  ? "error"
                                  : ""
                              }
                            >
                              {
                                translation.confinementResultReport.form
                                  .dailyOperationalCostMode
                              }
                              *
                            </label>
                          </Row>
                          <Row type="flex" justify="start" gutter={8}>
                            <Col span={12}>
                              <Radio.Group
                                value={values.dailyOperationalCostMode}
                                onChange={(e) => {
                                  setFieldValue(
                                    "dailyOperationalCostMode",
                                    e.target.value,
                                  );
                                  if (e.target.value !== "USER_DEFINED") {
                                    setFieldValue(
                                      "dailyOperationalCostValue",
                                      null,
                                    );
                                  }
                                }}
                              >
                                <Radio value="BOVEXO_COSTING">
                                  {
                                    translation.confinementResultReport.form
                                      .dailyOperationalCostModeOptions
                                      .BOVEXO_COSTING
                                  }
                                </Radio>
                                <Radio value="USER_DEFINED">
                                  {
                                    translation.confinementResultReport.form
                                      .dailyOperationalCostModeOptions
                                      .USER_DEFINED
                                  }
                                </Radio>
                              </Radio.Group>
                            </Col>
                            {values.dailyOperationalCostMode ===
                              "USER_DEFINED" && (
                              <Col span={12}>
                                <NumberFormat
                                  customInput={Input}
                                  value={
                                    values.dailyOperationalCostValue != null
                                      ? values.dailyOperationalCostValue
                                      : ""
                                  }
                                  className={
                                    errors.dailyOperationalCostValue &&
                                    submitCount > 0
                                      ? "error"
                                      : ""
                                  }
                                  decimalScale={2}
                                  decimalSeparator=","
                                  thousandSeparator="."
                                  fixedDecimalScale
                                  allowNegative={false}
                                  addonBefore="R$ "
                                  placeholder={translation.defaultPlaceholder}
                                  onValueChange={({ floatValue }) =>
                                    setFieldValue(
                                      "dailyOperationalCostValue",
                                      floatValue != null ? floatValue : null,
                                    )
                                  }
                                />
                              </Col>
                            )}
                          </Row>
                        </Col>
                      </Row>
                      {/* e. Custos Adicionais de Abate */}
                      <Row gutter={8}>
                        <Col span={12}>
                          <label>
                            {
                              translation.confinementResultReport.form
                                .additionalSlaughterCostValue
                            }
                          </label>
                          <NumberFormat
                            customInput={Input}
                            value={
                              values.additionalSlaughterCostValue != null
                                ? values.additionalSlaughterCostValue
                                : ""
                            }
                            decimalScale={2}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale
                            allowNegative={false}
                            addonBefore="R$ "
                            placeholder={translation.defaultPlaceholder}
                            onValueChange={({ floatValue }) =>
                              setFieldValue(
                                "additionalSlaughterCostValue",
                                floatValue != null ? floatValue : null,
                              )
                            }
                          />
                        </Col>
                      </Row>
                      {/* enableLotResult */}
                      <Row type="flex" justify="start" gutter={8}>
                        <Col span={16}>
                          <Row>
                            <label>Habilitar Resultado por Lote?</label>
                          </Row>
                          <Row type="flex">
                            <Switch
                              checked={values.enableLotResult}
                              onChange={(checked) => {
                                setFieldValue("enableLotResult", checked);
                                setFieldValue("showDeathData", checked);
                              }}
                            />
                          </Row>
                        </Col>
                      </Row>
                      {values.enableLotResult && (
                        <>
                          {/* lotIds */}
                          <Row type="flex" justify="start" gutter={8}>
                            <Col span={24}>
                              <Row>
                                <label
                                  className={
                                    errors.lotIds && submitCount > 0
                                      ? "error"
                                      : ""
                                  }
                                >
                                  {
                                    translation.confinementResultReport.form
                                      .lotIds
                                  }
                                  *
                                </label>
                              </Row>
                              <Row type="flex">
                                <Select
                                  name="lotIds"
                                  mode="multiple"
                                  value={values.lotIds || undefined}
                                  placeholder={
                                    translation.defaultSelectPlaceholder
                                  }
                                  onChange={(pValues) => {
                                    setFieldValue("lotIds", pValues);
                                  }}
                                >
                                  {lots.map((c) => (
                                    <Select.Option value={c.id} key={c.id}>
                                      {c.name}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </Row>
                            </Col>
                          </Row>
                          {/* showDeathData */}
                          <Row type="flex" justify="start" gutter={8}>
                            <Col span={16}>
                              <Row>
                                <label>
                                  {
                                    translation.confinementResultReport.form
                                      .showDeathData
                                  }
                                </label>
                              </Row>
                              <Row type="flex">
                                <Switch
                                  checked={values.showDeathData}
                                  onChange={(checked) =>
                                    setFieldValue("showDeathData", checked)
                                  }
                                />
                              </Row>
                            </Col>
                          </Row>
                        </>
                      )}
                      {/* showOriginData */}
                      <Row type="flex" justify="start" gutter={8}>
                        <Col span={16}>
                          <Row>
                            <label>
                              {
                                translation.confinementResultReport.form
                                  .showOriginData
                              }
                            </label>
                          </Row>
                          <Row type="flex">
                            <Switch
                              checked={values.showOriginData}
                              onChange={(checked) =>
                                setFieldValue("showOriginData", checked)
                              }
                            />
                          </Row>
                        </Col>
                      </Row>
                    </div>
                    {/* Footer */}
                    <Row type="flex">
                      <Col span={24}>
                        <Row
                          type="flex"
                          justify="end"
                          align="middle"
                          gutter={8}
                        >
                          <Col>
                            <ButtonStandard
                              type="button"
                              buttonType="type4"
                              size="s"
                              onClick={handleCancel}
                            >
                              {translation.buttons.cancel}
                            </ButtonStandard>
                          </Col>
                          <Col>
                            <ButtonStandard
                              type="submit"
                              buttonType="type1"
                              size="s"
                            >
                              {translation.buttons.generate}
                            </ButtonStandard>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </form>
                )}
              />
            </Col>
          </Row>
        </Spin>
      </Container>
    </Modal>
  );
};

export default ConfinementResultReportModal;
